import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiKey, FiGlobe, FiZap, FiCheckCircle, FiAlertTriangle, FiChevronDown, FiInfo, FiUsers, FiCpu, FiLayers, FiArrowLeft, FiList, FiStar, FiRotateCcw, FiClock, FiTrash2 } from 'react-icons/fi';
import { cloudflareGroups } from './data';
import { TEMPLATES } from './templates'; 
import './App.css';

function App() {
  // State
  const [apiToken, setApiToken] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Zone list (fetched from account when token is entered)
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const tokenDebounceRef = useRef(null);

  const [zoneMeta, setZoneMeta] = useState(null);
  const [settings, setSettings] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(cloudflareGroups[0].title);
  const [expandedBlueprint, setExpandedBlueprint] = useState(null);

  // Blueprint tab: 'se' | 'tase'
  const [blueprintTab, setBlueprintTab] = useState('se');

  // Deployment history + rollback state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Dynamic templates — can be overridden by Helix Security Advisors deep-link
  const [activeTemplates, setActiveTemplates] = useState(TEMPLATES);

  // Read URL params on mount (Helix Security Advisors deep-link support)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlZoneId = params.get('zone_id');
    const urlFixes = params.get('fixes');

    if (urlZoneId) {
      setZoneId(urlZoneId);
    }

    if (urlFixes) {
      try {
        const fixesJson = JSON.parse(atob(urlFixes));
        const helixTemplate = {
          id: "helix_security_fixes",
          name: "🔧 Helix Security Advisors Fixes",
          description: "Auto-generated fixes from Helix Security Advisors.",
          benefits: [
            `✅ ${Object.keys(fixesJson).length} settings to remediate`,
            "✅ Generated from Helix Security Advisors scan",
            "✅ Maps to detected misconfigurations",
            "✅ Apply to bring zone to optimal security posture"
          ],
          rules: fixesJson
        };
        // Replace WordPress Secure (index 3) with Helix Security Advisors Fixes
        setActiveTemplates(prev => {
          const updated = [...prev];
          updated[3] = helixTemplate;
          return updated;
        });
        // Auto-apply the Helix Security Advisors fixes to the settings panel
        setSettings(prev => ({ ...prev, ...fixesJson }));
        setLogs(prev => [...prev, `🔧 Helix Security Advisors Fixes loaded: ${Object.keys(fixesJson).length} settings pre-configured`]);
      } catch (e) {
        console.error('[Helix] Failed to parse Helix Security Advisors fixes:', e);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use environment variable for API URL, fallback to localhost for development
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

  // Fetch zones whenever token changes (debounced 600ms so we don't hammer on each keystroke)
  useEffect(() => {
    if (tokenDebounceRef.current) clearTimeout(tokenDebounceRef.current);
    if (!apiToken || apiToken.length < 20) {
      setZones([]);
      setZoneId('');
      return;
    }
    tokenDebounceRef.current = setTimeout(async () => {
      setZonesLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/zones`, {
          headers: { 'Authorization': `Bearer ${apiToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setZones(data);
          // Auto-select first zone if none chosen
          if (data.length > 0 && !zoneId) setZoneId(data[0].id);
        } else {
          setZones([]);
        }
      } catch (e) {
        setZones([]);
      } finally {
        setZonesLoading(false);
      }
    }, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken]);

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${apiToken}`
  });

  const applyTemplate = (template) => {
    if (window.confirm(`Apply template "${template.name}"? This will overwrite current screen settings.`)) {
      setSettings(prev => ({ ...prev, ...template.rules }));
      setLogs(prev => [...prev, `📋 Template applied: ${template.name}`]);
    }
  };

  const fetchHistory = useCallback(async () => {
    if (!apiToken) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/history`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (e) {
      console.error('Failed to fetch history:', e);
    } finally {
      setHistoryLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken]);

  // Refresh history whenever token changes or after a deploy completes
  useEffect(() => {
    if (apiToken && showHistory) fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken, showHistory]);

  const handleRollback = async (jobId, zoneName) => {
    if (!apiToken) return alert("API Token is required to rollback");
    if (!window.confirm(`Rollback zone "${zoneName}" to the state before deployment ${jobId}?\n\nThis will re-deploy the pre-deploy snapshot captured at deploy time.`)) return;

    setRollingBack(jobId);
    setLogs([`↩️ Starting rollback of job ${jobId} on zone "${zoneName}"...`]);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/rollback/${jobId}`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Rollback request failed');
      }

      const { jobId: rollbackJobId } = await response.json();
      setLogs(prev => [...prev, `📋 Rollback Job ID: ${rollbackJobId}`, `⏳ Monitoring rollback progress...`]);

      // Poll for rollback completion — same mechanism as a normal deploy
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/deploy-status/${rollbackJobId}`, {
            headers: getHeaders()
          });
          if (!statusRes.ok) {
            clearInterval(pollInterval);
            setLogs(prev => [...prev, "❌ Lost connection to rollback job"]);
            setLoading(false);
            setRollingBack(null);
            return;
          }
          const statusData = await statusRes.json();
          setLogs(statusData.logs || []);

          if (statusData.status === 'completed' || statusData.status === 'failed') {
            clearInterval(pollInterval);
            setLoading(false);
            setRollingBack(null);
            if (statusData.status === 'completed') {
              setLogs(prev => [...prev, "✅ Rollback completed successfully!"]);
              fetchHistory(); // Refresh history to show the new rollback entry
            } else {
              setLogs(prev => [...prev, "❌ Rollback failed. Check logs above for details."]);
            }
          }
        } catch (pollError) {
          clearInterval(pollInterval);
          setLogs(prev => [...prev, "❌ Polling error: " + pollError.message]);
          setLoading(false);
          setRollingBack(null);
        }
      }, 2000);

    } catch (error) {
      setLogs(prev => [...prev, "❌ Rollback Error: " + error.message]);
      setLoading(false);
      setRollingBack(null);
    }
  };

  const handleDeleteHistory = async (jobId) => {
    if (!window.confirm(`Remove job #${jobId} from history? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/history/${jobId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      setHistory(prev => prev.filter(j => j.jobId !== jobId));
    } catch (e) {
      console.error('Failed to delete history entry:', e);
    }
  };

  const handleScan = async () => {
    if (!apiToken) return alert("API Token is required");
    if (!zoneId) return alert("Select a zone from the dropdown");
    setScanning(true);
    setZoneMeta(null);
    setSettings({}); 
    try {
      try {
          const metaRes = await fetch(`${API_URL}/api/zone-info/${zoneId}`, { headers: getHeaders() });
          if(metaRes.ok) { const meta = await metaRes.json(); setZoneMeta(meta); }
      } catch(e) { }
      const res = await fetch(`${API_URL}/api/scan/${zoneId}`, { headers: getHeaders() });
      if (!res.ok) throw new Error("Scan failed or Access Denied.");
      const data = await res.json();
      setSettings(prev => ({ ...prev, ...data }));
    } catch (err) { alert("❌ Error: " + err.message); } finally { setScanning(false); }
  };

  const handleDeploy = async () => {
    if (!apiToken || !zoneId) return alert("Token and Zone ID are required");
    setLoading(true);
    setLogs(["🚀 Initializing Deployment Protocol..."]);
    try {
      // Start deployment and get jobId
      const response = await fetch(`${API_URL}/api/deploy`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ targetZoneId: zoneId, settings })
      });
      
      if (!response.ok) throw new Error("Deployment request failed");
      
      const { jobId } = await response.json();
      setLogs(prev => [...prev, `📋 Job ID: ${jobId}`, `⏳ Monitoring deployment progress...`]);
      
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/deploy-status/${jobId}`, {
            headers: getHeaders()
          });
          
          if (!statusRes.ok) {
            clearInterval(pollInterval);
            setLogs(prev => [...prev, "❌ Lost connection to deployment job"]);
            setLoading(false);
            return;
          }
          
          const statusData = await statusRes.json();
          
          // Update logs with latest from backend
          setLogs(statusData.logs || []);
          
          // Check if deployment finished
          if (statusData.status === 'completed' || statusData.status === 'failed') {
            clearInterval(pollInterval);
            setLoading(false);
            if (statusData.status === 'completed') {
              setLogs(prev => [...prev, "✅ Deployment completed successfully!"]);
              fetchHistory(); // Refresh history panel to show the new entry
            } else {
              setLogs(prev => [...prev, "❌ Deployment failed. Check logs above for details."]);
            }
          }
        } catch (pollError) {
          clearInterval(pollInterval);
          setLogs(prev => [...prev, "❌ Polling error: " + pollError.message]);
          setLoading(false);
        }
      }, 2000); // Poll every 2 seconds
      
    } catch (error) { 
      setLogs(prev => [...prev, "❌ Connection Error: " + error.message]); 
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const val = settings[field.id];
    let isOptimal = true;
    if (field.recommended !== undefined) {
        let currentBool = val === "on" || val === true;
        let recommendedBool = field.recommended === true;
        if (field.type === 'toggle') isOptimal = currentBool === recommendedBool;
        else isOptimal = val === field.recommended;
    }
    return (
      <div key={field.id} className="setting-row">
        <div className="setting-info">
            {field.recommended !== undefined && (isOptimal ? <FiCheckCircle className="audit-icon audit-success" /> : <FiAlertTriangle className="audit-icon audit-warning" />)}
            <span className="setting-label">{field.label}</span>
            <div className="tooltip-container"><FiInfo size={16} /><span className="tooltip-text">{field.help}</span></div>
        </div>
        <div className="setting-input">
            {field.type === 'toggle' ? (
              <label className="switch">
                <input type="checkbox" checked={val === "on" || val === true} onChange={(e) => setSettings({...settings, [field.id]: e.target.checked ? "on" : "off"})} />
                <span className="slider"></span>
              </label>
            ) : field.type === 'select' ? (
              <select className="select-modern" value={val || field.default} onChange={(e) => setSettings({...settings, [field.id]: e.target.value})}>
                {field.options.map(opt => (<option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>))}
              </select>
            ) : (
              <input className="input-text-modern" type="text" value={val || field.default} onChange={(e) => setSettings({...settings, [field.id]: e.target.value})} />
            )}
        </div>
      </div>
    );
  };

  // --- HELP VIEW RENDER ---
  if (showHelp) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-text">
              <h1><span className="brand-highlight">Blueprint</span> Docs</h1>
              <p>Understanding Automation Templates</p>
          </div>
          <button className="btn-modern" onClick={() => setShowHelp(false)}>
            <FiArrowLeft /> Back to Dashboard
          </button>
        </header>

        <div className="help-grid">
          {activeTemplates.map(t => (
            <div key={t.id} className="help-card">
              <div className="help-card-header">
                <h3>{t.name}</h3>
                <p>{t.description}</p>
              </div>
              
              <div className="help-section">
                <div className="help-subtitle"><FiStar color="var(--brand-primary)"/> Key Benefits</div>
                <ul className="help-list">
                  {t.benefits.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>

              <div className="help-section">
                <div className="help-subtitle"><FiList color="var(--text-secondary)"/> Technical Configuration</div>
                <div className="tech-specs">
                  {Object.entries(t.rules).map(([key, value]) => {
                    // Format key: capitalize first letter of each word
                    const formattedKey = key
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');
                    
                    // Format value: show on/off as badges, others as text
                    const formattedValue = String(value).toLowerCase() === 'on' 
                      ? '✓ Enabled' 
                      : String(value).toLowerCase() === 'off' 
                        ? '✗ Disabled' 
                        : String(value);
                    
                    return (
                      <div key={key} className="spec-row">
                        <span className="spec-key">{formattedKey}</span>
                        <span className="spec-value" style={{
                          color: String(value).toLowerCase() === 'on' ? '#10b981' : 
                                 String(value).toLowerCase() === 'off' ? '#6b7280' : 
                                 'var(--text-primary)',
                          fontWeight: String(value).toLowerCase() === 'on' || String(value).toLowerCase() === 'off' ? '600' : '500'
                        }}>{formattedValue}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- MAIN DASHBOARD RENDER ---
  return (
    <div className="app-container">
      <header className="header">
        <h1>
          <span className="header-title-es">Project</span>
          <span className="header-title-en">Project</span>
          {' '}<span className="brand-highlight">Helix for Apps</span>
        </h1>
        <div className="header-actions">
            <button
              className="btn-history"
              onClick={() => { setShowHistory(h => !h); if (!showHistory) fetchHistory(); }}
              title="Deployment History & Rollback"
            >
              <FiClock size={16} />
              <span>History</span>
            </button>
            <button 
              className="btn-jovi" 
              onClick={() => setShowHelp(true)} 
              title="Ask JOVI - Blueprint Documentation & Help"
            >
                <img 
                  src="https://imagedelivery.net/KJB-rRwHD6dkMpOM_8NFVQ/9acca0c9-f63c-43c5-6ee7-a79a1a8fce00/CorreosPrincipal" 
                  alt="JOVI" 
                  className="jovi-avatar"
                />
                <span>Ask JOVI</span>
            </button>
            <img src="https://imagedelivery.net/KJB-rRwHD6dkMpOM_8NFVQ/dfd83a02-5bf0-4faa-ab04-c5158a329d00/Landscape" alt="Helix Logo" className="header-logo" />
        </div>
      </header>
      <p className="header-subtitle">Independent Infrastructure Auditor</p>

      <div className="hero-bar inputs-row">
        <div className="input-group-modern">
            <FiKey className="input-icon" />
            <input
              type="password"
              className="input-modern"
              placeholder="Paste API Token"
              value={apiToken}
              onChange={e => setApiToken(e.target.value)}
            />
        </div>
        <div className="input-group-modern zone-select-group">
            <FiGlobe className="input-icon" />
            {zones.length > 0 ? (
              <select
                className="input-modern zone-select"
                value={zoneId}
                onChange={e => setZoneId(e.target.value)}
              >
                {zones.map(z => (
                  <option key={z.id} value={z.id}>
                    {z.name}{z.plan && z.plan !== 'Unknown' ? ` (${z.plan})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="input-modern"
                placeholder={zonesLoading ? "Loading zones..." : "Enter token to load zones"}
                value=""
                readOnly
              />
            )}
            {zonesLoading && <span className="zone-loading-spinner" />}
        </div>
        <button className="btn-modern btn-scan" onClick={handleScan} disabled={scanning || !zoneId}>
          {scanning ? "Processing..." : "Audit Zone"}
        </button>
      </div>

      <AnimatePresence>
        {zoneMeta && (
            <motion.div className="zone-meta-card" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="meta-item"><FiGlobe className="meta-icon" /><div><span className="meta-label">Domain</span><span className="meta-value">{zoneMeta.name}</span></div></div>
                <div className="meta-item"><FiUsers className="meta-icon" /><div><span className="meta-label">Account</span><span className="meta-value">{zoneMeta.account}</span></div></div>
                <div className="meta-item"><FiCpu className="meta-icon" /><div><span className="meta-label">Plan</span><span className="meta-value badge-plan">{zoneMeta.plan}</span></div></div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* ── Deployment History Panel ── */}
      <AnimatePresence initial={false}>
        {showHistory && (
          <motion.div
            className="history-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="history-header">
              <div className="history-title"><FiClock size={16}/> Deployment History</div>
              <button className="btn-refresh" onClick={fetchHistory} disabled={historyLoading}>
                {historyLoading ? 'Loading...' : '↻ Refresh'}
              </button>
            </div>

            {!apiToken && (
              <p className="history-empty">Enter your API token above to load history.</p>
            )}

            {apiToken && history.length === 0 && !historyLoading && (
              <p className="history-empty">No deployments recorded yet.</p>
            )}

            {history.length > 0 && (
              <div className="history-list">
                {history.map(job => {
                  const date = job.completedAt
                    ? new Date(job.completedAt).toLocaleString()
                    : new Date(Number(job.jobId)).toLocaleString();
                  const isThisRollingBack = rollingBack === job.jobId;
                  const canRollback = job.hasSnapshot && job.status === 'completed' && !loading;
                  const hasSnapshotWarnings = job.snapshotWarnings && job.snapshotWarnings.length > 0;

                  return (
                    <div key={job.jobId} className={`history-item ${job.isRollback ? 'history-item--rollback' : ''}`}>
                      <div className="history-item-main">
                        <div className="history-item-info">
                          <span className={`history-status history-status--${job.status}`}>
                            {job.status === 'completed' ? '✅' : job.status === 'failed' ? '❌' : '⏳'} {job.status}
                          </span>
                          <span className="history-zone">{job.zoneName || job.zoneId || '—'}</span>
                          {job.isRollback && (
                            <span className="history-badge-rollback">↩ rollback of {job.rollbackOf}</span>
                          )}
                        </div>
                        <div className="history-item-meta">
                          <span className="history-date">{date}</span>
                          <span className="history-jobid">#{job.jobId}</span>
                        </div>
                      </div>
                      <div className="history-item-actions">
                        <button
                          className={`btn-undo ${hasSnapshotWarnings ? 'btn-undo--warned' : ''}`}
                          onClick={() => handleRollback(job.jobId, job.zoneName || job.zoneId)}
                          disabled={!canRollback || !!rollingBack}
                          title={
                            !job.hasSnapshot
                              ? 'No pre-deploy snapshot available (job predates rollback support)'
                              : hasSnapshotWarnings
                                ? `⚠️ Snapshot incomplete:\n${job.snapshotWarnings.join('\n')}`
                                : canRollback
                                  ? 'Rollback zone to state before this deployment'
                                  : 'Cannot rollback'
                          }
                        >
                          <FiRotateCcw size={14} />
                          {isThisRollingBack ? 'Rolling back...' : hasSnapshotWarnings ? '⚠️ Undo' : 'Undo'}
                        </button>
                        <button
                          className="btn-delete-history"
                          onClick={() => handleDeleteHistory(job.jobId)}
                          disabled={!!rollingBack || !!loading}
                          title="Remove from history"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="blueprints-section">
        <div className="blueprints-header">
          <div className="section-title"><FiLayers /> Available Blueprints</div>
          <div className="blueprint-tabs">
            <button
              className={`blueprint-tab ${blueprintTab === 'se' ? 'blueprint-tab--active' : ''}`}
              onClick={() => setBlueprintTab('se')}
            >
              SE Templates
            </button>
            <button
              className={`blueprint-tab ${blueprintTab === 'tase' ? 'blueprint-tab--active' : ''}`}
              onClick={() => setBlueprintTab('tase')}
            >
              TASE Templates
            </button>
          </div>
        </div>

        {(() => {
          const visibleTemplates = activeTemplates.filter(t =>
            (t.category || 'se') === blueprintTab
          );
          return (
            <>
              {/* Desktop: Grid Layout */}
              <div className="blueprints-grid">
                {visibleTemplates.map(t => (
                  <div key={t.id} className="template-card">
                    <div>
                      <h4>{t.name}</h4>
                      <p>{t.description}</p>
                    </div>
                    <button className="btn-small" onClick={() => applyTemplate(t)}>
                      Apply Template
                    </button>
                  </div>
                ))}
              </div>

              {/* Mobile: Accordion Layout */}
              <div className="blueprints-accordion">
                {visibleTemplates.map(t => {
                  const isOpen = expandedBlueprint === t.id;
                  return (
                    <div key={t.id} className="blueprint-accordion-item">
                      <button
                        className="blueprint-accordion-header"
                        onClick={() => setExpandedBlueprint(isOpen ? null : t.id)}
                      >
                        <h4>{t.name}</h4>
                        <FiChevronDown
                          size={20}
                          className="accordion-icon"
                          style={{
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.3s ease"
                          }}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                              open: { opacity: 1, height: "auto" },
                              collapsed: { opacity: 0, height: 0 }
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="blueprint-accordion-content">
                              <p>{t.description}</p>
                              <button className="btn-small" onClick={() => applyTemplate(t)}>
                                Apply Template
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      <div className="sections-container">
        {cloudflareGroups.map((group) => {
            const isOpen = expandedGroup === group.title;
            return (
                <div key={group.title} className="accordion-item">
                    <button className="accordion-header" onClick={() => setExpandedGroup(isOpen ? false : group.title)}>
                        <div className="header-content"><h3>{group.title}</h3><p>{group.description}</p></div>
                        <FiChevronDown size={24} className="accordion-icon" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                    </button>
                    <AnimatePresence initial={false}>{isOpen && (<motion.div initial="collapsed" animate="open" exit="collapsed" variants={{ open: { opacity: 1, height: "auto" }, collapsed: { opacity: 0, height: 0 } }} transition={{ duration: 0.3 }}><div>{group.fields.map(renderField)}</div></motion.div>)}</AnimatePresence>
                </div>
            );
        })}
      </div>

      <button className="btn-modern btn-deploy" onClick={handleDeploy} disabled={loading}><FiZap size={24} />{loading ? "Deploying Configuration..." : "Deploy Changes to Cloudflare"}</button>

      {logs.length > 0 && (
        <div className="terminal-container">
            <div className="terminal-header"><div className="terminal-dot dot-red"></div><div className="terminal-dot dot-yellow"></div><div className="terminal-dot dot-green"></div><span style={{marginLeft: '10px', fontWeight: 600}}>Logs</span></div>
            <div className="logs-output">{logs.map((log, i) => <div key={i} className="log-line">> {log}</div>)}</div>
        </div>
      )}
    </div>
  );
}

export default App;
